import i18n from "@/plugins/i18n"
import Config from "@/config";

/**
 * Дата в формате ISO 8601 (2005-08-15T15:52:01+00:00)
 */
type ParseableDateTime = string;

interface FormatCases {
  nom: string
  gen: string
  plu: string
}

const FORMAT_CASES_DAY: FormatCases = {nom: 'день', gen: 'дня', plu: 'дней'};

/**
 * Отформатировать дату с указанными параметрами.
 * @param date
 * @param opts
 * @return String
 */
function formatDateFromOptions(date: Date, opts: any): string {
  // Intl.DateTimeFormatOptions убрано из типов, см
  // https://github.com/microsoft/TypeScript/issues/35865
  let isIe = false;
  try {
    const tmz = Config.get('config')?.serverTimeZone ?? 'GMT+3';
    // в IE эта строчка падает, т.к там поддерживается только UTC
    new Date().toLocaleString([], { timeZone: tmz });
  } catch (e) {
    isIe = true;
    delete opts.timeZoneName; // уберем часовой пояс, все равно он не проставится
  }
  let timeStr = new Intl.DateTimeFormat([], opts).format(date);
  if (isIe) {
    // добавим смещение, т.к в IE его нет.
    let offset = date.getTimezoneOffset();
    let absOffset = Math.abs(offset);
    timeStr += ' GMT ' + (offset < 0 ? '+' : '-');
    timeStr +=
      ('00' + Math.floor(absOffset / 60)).slice(-2) +
      ':' +
      ('00' + (absOffset % 60)).slice(-2);
  }
  return timeStr;
}

/**
 * Отформатировать дату
 * @param dateStr
 * @return string
 */
function formatDateTime(dateStr?: ParseableDateTime): string {
  if (!dateStr) {
    return '';
  }
  let date = new Date(Date.parse(dateStr));
  let opts = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    timeZoneName: 'short',
  };
  return formatDateFromOptions(date, opts);
}

/**
 * Отформатировать дату без часового пояса
 * @param dateStr
 * @return string
 */
function formatDateTimeNoTz(dateStr?: ParseableDateTime): string {
  if (!dateStr) {
    return '';
  }
  let date = new Date(Date.parse(dateStr));
  let opts = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  };
  return formatDateFromOptions(date, opts);
}

/**
 * Отформатировать дату без времени и час. пояса
 * @param dateStr
 * @return string
 */
function formatDate(dateStr?: ParseableDateTime): string {
  if (!dateStr) {
    return '';
  }
  let date = new Date(Date.parse(dateStr));
  let opts = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  };
  return formatDateFromOptions(date, opts);
}

/**
 * Формат даты с временем
 * @param dateStr
 * @return string
 */
function formatDateWithOnlyTime(dateStr?: ParseableDateTime): string {
  if (!dateStr) {
    return '';
  }
  let date = new Date(Date.parse(dateStr));
  let opts = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  };
  return formatDateFromOptions(date, opts);
}

/**
 * Формат даты без секунд.
 * @param dateStr
 * @return string
 */
function formatMinuteTime(dateStr?: ParseableDateTime): string {
  if (!dateStr) {
    return '';
  }
  let date = new Date(Date.parse(dateStr));
  let opts = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short',
  };
  return formatDateFromOptions(date, opts);
}

/**
 * Формат даты без секунд.
 * @param dateStr
 * @return string
 */
function formatMinuteTimeNoTZ(dateStr?: ParseableDateTime): string {
  if (!dateStr) {
    return '';
  }
  let date = new Date(Date.parse(dateStr));
  let opts = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  };
  return formatDateFromOptions(date, opts).replace(',', '');
}

/**
 * Человекочитаемый размер файла.
 *
 * @param {Number} bytes
 * @param {Number} [decimals]
 * @return string
 */
function formatByteSize(bytes, decimals?): string {
  if (bytes == 0) {
    return i18n.global.t('0 Б');
  }
  let k = 1024;
  let dm = decimals || 2;
  let sizes = [
    'Байт',
    'Кбайт',
    'Мбайт',
    'ГБайт',
    'Тбайт',
    'Пбайт',
    'Эбайт',
    'Збайт',
    'Ибайт',
  ];
  let i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + i18n.global.t(sizes[i]);
}

/**
 * Форматирование числа с пробелами через 3 порядка.
 *
 * @param {Number} num
 * @return string
 */
function splitNumber(num): string {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/**
 * Возвращает слово, следующее за счётчиком, с учётом палежа.
 *
 * @param {Number} num      Число
 * @param {Object} cases    Варианты слова {nom: 'час', gen: 'часа', plu: 'часов'}
 * nom — Nominativ, именительный падеж
 * gen — Genetiv, родительный падеж
 * plu — Plural, множественное число.
 * @return {String}
 */
function formatCases(num: number, cases: FormatCases): string {
  num = Math.abs(num);

  let word = '';

  if (num.toString().indexOf('.') > -1) {
    word = cases.gen;
  } else {
    word =
      num % 10 == 1 && num % 100 != 11
        ? cases.nom
        : num % 10 >= 2 && num % 10 <= 4 && (num % 100 < 10 || num % 100 >= 20)
        ? cases.gen
        : cases.plu;
  }

  return i18n.global.t(word);
}

/**
 * Отформатировать начальную цену.
 * @param value Значение цены.
 * @param showZero Показывать "0,00" при value = 0.
 * @return string
 */
function formatStartPrice(value: string | number, showZero = false): string {
  if ((value === 0 || value === '0') && !showZero) {
    return '-';
  }

  const fixed = Number(value).toFixed(2);
  const [integerPart, decimalPart] = fixed.split('.');

  const formattedInteger = splitNumber(integerPart);

  return `${formattedInteger},${decimalPart}`;
}

/**
 * Задает форматирование цены + добавляет символ валюты + подпись наличия признака НДС
 * @param value Значение цены.
 * @param ndsFlag Флаг отображения НДС.
 * @param currencyFlag Флаг отображения валюты.
 * @return string
 */
function formatMoneyText(value, ndsFlag, currencyFlag = true) {
  let price = formatStartPrice(value);
  if (currencyFlag) price += ' $';
  price += (typeof ndsFlag === "boolean") ? (ndsFlag ? ' ' + i18n.global.t('(с НДС)') : ' ' + i18n.global.t('(без НДС)')) : '';
  return price;
}

/**
 * Отформатировать цену
 * @param value
 * @return string
 */
function formatPrice(value): string {
  return Number(value).toLocaleString("ru", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Форматирует количество дней с правильным склонением
 * @param days
 * @returns string
 */
function formatDays(days): string {
  if (days === null || days === undefined) return '-';

  const n = Math.abs(days) % 100;
  const n1 = n % 10;

  let key = '{_1} дней';

  if (n > 10 && n < 20) {
    key = '{_1} дней';
  } else if (n1 === 1) {
    key = '{_1} день';
  } else if (n1 >= 2 && n1 <= 4) {
    key = '{_1} дня';
  }

  return i18n.global.t(key, { _1: days });
}

/**
 * Количество дней до определенной даты
 * @param targetDate
 * @return string
 */
function daysUntil(targetDate) {
  const today = new Date();
  const target = new Date(targetDate);

  const differenceInTime = target.getTime() - today.getTime();

  const differenceInDays = Math.ceil(differenceInTime / (1000 * 3600 * 24));

  return differenceInDays;
}

/**
 * Форматирует ставку НДС.
 */
function formatRate(rate: string|null): string {
  if (rate === null) return i18n.global.t('Без НДС');

  const num = parseFloat(rate);
  return Number.isInteger(num) ? `${num}` : `${num.toFixed(2)}`;
}

export {
  formatDateTime,
  formatDate,
  formatMinuteTime,
  formatDateFromOptions,
  formatByteSize,
  formatDateTimeNoTz,
  formatRate,
  splitNumber,
  formatCases,
  formatStartPrice,
  formatPrice,
  formatDateWithOnlyTime,
  daysUntil,
  formatMinuteTimeNoTZ,
  formatMoneyText,
  formatDays,
  FORMAT_CASES_DAY,
};
